'use client';

import { useCheckGiftXHandle } from '@morfi/backend-sdk-v2/client';
import { useEffect, useState } from 'react';
export type XHandleStatus = 'idle' | 'checking' | 'found' | 'not_found' | 'self' | 'invalid_format' | 'unavailable' | 'network_error';
export interface UseXHandleCheckResult {
  status: XHandleStatus;
  xIdStr: string | null;
  canonicalScreenName: string | null;
}
const X_HANDLE_REGEX: RegExp = /^[A-Za-z0-9_]{1,15}$/;
const DEBOUNCE_MS = 400;
interface ApiSuccess {
  exists: boolean;
  xIdStr?: string;
  canonicalScreenName?: string;
}
interface CheckedXHandleResult extends UseXHandleCheckResult {
  handle: string;
}

// ALLOW-UNKNOWN: type guard narrowing arbitrary thrown values from the SDK.
function isObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object';
}

// ALLOW-UNKNOWN: SDK rejections may be Error, ApiError envelope, or raw object.
function extractErrorCode(err: unknown): string | null {
  if (!isObject(err)) return null;
  if (typeof err.code === 'string') return err.code;
  const data = err.data;
  if (isObject(data) && typeof data.code === 'string') return data.code;
  // ALLOW-UNKNOWN: nested error envelope shape from fetchWrapperWithMeta.
  const error = (err as {
    error?: unknown;
  }).error;
  // ALLOW-UNKNOWN: narrowing inner.code on the discriminated envelope.
  if (isObject(error) && typeof (error as {
    code?: unknown;
  }).code === 'string') {
    return (error as {
      code: string;
    }).code;
  }
  return null;
}

/**
 * Debounced X-handle validation hook.
 *
 * Calls `POST /v1/gifts/x-handle-check`, which proxies to the X API and
 * caches results server-side. Returns the canonical screen name + numeric
 * `id_str` so the on-chain `send_usdc_to_handle` instruction can include
 * the X user id (sufficient for handle-rotation safety per FR-093).
 */
type SyncValidity = 'empty' | 'invalid_format' | 'valid';
function classifyHandle(trimmed: string): SyncValidity {
  if (trimmed.length === 0) return 'empty';
  if (!X_HANDLE_REGEX.test(trimmed)) return 'invalid_format';
  return 'valid';
}
export function useXHandleCheck(handle: string): UseXHandleCheckResult {
  const trimmed = handle.trim().replace(/^@/, '');
  const validity = classifyHandle(trimmed);
  const [checked, setChecked] = useState<CheckedXHandleResult | null>(null);
  const checkGiftXHandle = useCheckGiftXHandle();
  const mutateAsync = checkGiftXHandle.mutateAsync;
  useEffect(() => {
    if (validity !== 'valid') return;
    let cancelled = false;
    const timer = setTimeout((): void => {
      void (async (): Promise<void> => {
        try {
          const result = await mutateAsync({
            handle: trimmed
          });
          if (cancelled) return;
          const data = ((result as {
            data?: ApiSuccess;
          }).data ?? result) as ApiSuccess;
          if (data.exists !== true) {
            setChecked({
              handle: trimmed,
              status: 'not_found',
              xIdStr: null,
              canonicalScreenName: null
            });
            return;
          }
          setChecked({
            handle: trimmed,
            status: 'found',
            xIdStr: data.xIdStr ?? null,
            canonicalScreenName: data.canonicalScreenName ?? trimmed
          });
        } catch (err: unknown) {
          // SILENT-CATCH: Validation errors map to UI status; no telemetry needed.
          if (cancelled) return;
          const code = extractErrorCode(err);
          const status: XHandleStatus = code === 'SELF_GIFT_BLOCKED' ? 'self' : code === 'X_HANDLE_NOT_FOUND' ? 'not_found' : code === 'X_API_UNAVAILABLE' || code === 'SYSTEM_SERVICE_UNAVAILABLE' ? 'unavailable' : code === 'INVALID_HANDLE_FORMAT' ? 'invalid_format' : 'network_error';
          setChecked({
            handle: trimmed,
            status,
            xIdStr: null,
            canonicalScreenName: null
          });
        }
      })();
    }, DEBOUNCE_MS);
    return (): void => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [validity, trimmed, mutateAsync]);
  if (validity === 'empty') {
    return {
      status: 'idle',
      xIdStr: null,
      canonicalScreenName: null
    };
  }
  if (validity === 'invalid_format') {
    return {
      status: 'invalid_format',
      xIdStr: null,
      canonicalScreenName: null
    };
  }
  if (checked?.handle !== trimmed) {
    return {
      status: 'checking',
      xIdStr: null,
      canonicalScreenName: null
    };
  }
  return checked;
}